import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, TrendingUp, Target, BarChart3, Users } from 'lucide-react';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import brandLogo from '../../assets/brand/logo.png';

const AuthBanner = ({ type = 'login' }) => {
  const isLogin = type === 'login';
  const { t } = usePublicLanguage();

  const features = [
    {
      icon: Target,
      title: t('auth.banner.features.matching.title'),
      description: t('auth.banner.features.matching.description')
    },
    {
      icon: BarChart3,
      title: t('auth.banner.features.analytics.title'),
      description: t('auth.banner.features.analytics.description')
    },
    {
      icon: Users,
      title: t('auth.banner.features.collaboration.title'),
      description: t('auth.banner.features.collaboration.description')
    }
  ];

  const stickerPalettes = [
    {
      background: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 100%)',
      borderColor: '#7dd3fc',
      color: '#0c4a6e',
      arrow: '#7dd3fc'
    },
    {
      background: 'linear-gradient(180deg, #fef3c7 0%, #fde68a 100%)',
      borderColor: '#fbbf24',
      color: '#78350f',
      arrow: '#fbbf24'
    },
    {
      background: 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%)',
      borderColor: '#4ade80',
      color: '#14532d',
      arrow: '#4ade80'
    }
  ];

  return (
    <div className={`relative h-full w-full flex items-center justify-center p-10 overflow-hidden auth-banner-whiteboard auth-banner-${type}`}>
      <div className="hero-whiteboard-grid" aria-hidden="true" />
      <div className="hero-whiteboard-glare" aria-hidden="true" />

      <div className="relative z-10 max-w-lg w-full auth-banner-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex justify-center mb-8">
            <div className="h-32 w-32 flex items-center justify-center auth-banner-brand-card">
              <img
                src={brandLogo}
                alt="Candio logo"
                className="h-full w-full object-cover rounded-2xl"
              />
            </div>
          </div>

          <h1 className="text-4xl font-bold mb-4 text-center auth-banner-title">
            {isLogin ? t('auth.banner.login.title') : t('auth.banner.signup.title')}
          </h1>
          <p className="text-lg mb-8 text-center auth-banner-subtitle">
            {isLogin ? t('auth.banner.login.subtitle') : t('auth.banner.signup.subtitle')}
          </p>

          <div className="space-y-4 text-left">
            {features.map((feature, index) => {
              const palette = stickerPalettes[index % stickerPalettes.length];
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className={`flex items-start space-x-3 rounded-lg p-3 auth-banner-sticker auth-banner-sticker-r${(index % 3) + 1}`}
                  style={{
                    background: palette.background,
                    borderColor: palette.borderColor,
                    color: palette.color,
                    '--auth-sticker-arrow': palette.arrow
                  }}
                >
                  <feature.icon className="h-5 w-5 mt-1 auth-banner-icon" />
                  <div>
                    <p className="text-sm font-semibold auth-banner-sticker-title">{feature.title}</p>
                    <p className="text-xs auth-banner-sticker-body">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-8 right-8">
        <Sparkles className="h-8 w-8 auth-banner-deco-icon" />
      </div>
      <div className="absolute bottom-8 left-8">
        <TrendingUp className="h-8 w-8 auth-banner-deco-icon" />
      </div>
    </div>
  );
};

export default AuthBanner;
